<script setup lang="ts">
/**
 * Zentrales Icon-Mapping (Konvention aus adaptivedit/docs/konventionen.md).
 *
 * Genutzt wird `@lucide/vue` als NPM-Abhängigkeit, nie ein CDN, und immer über
 * diese Komponente: `<Icon name="produkte" :size="16" />`. Ein neues Icon wird HIER
 * registriert — Tree-Shaking packt nur die tatsächlich benutzten ins Bundle, ein
 * zusaetzlicher Eintrag ist also billig.
 *
 * (adaptivedit nutzt noch das Vorgaengerpaket `lucide-vue-next`. Das ist inzwischen
 * abgekuendigt; hier steht der Nachfolger — gleiche Icons, gleiche Namen.)
 *
 * Die Schlüssel sind fachlich benannt (`produkte`, `roadmap`), nicht nach der Form
 * des Bildchens. So bleibt die Bedeutung eindeutig, und ein Icon-Tausch ist eine
 * Änderung an genau einer Zeile. Keine Emojis als UI-Icons.
 */
import type { Component } from 'vue';
import { computed } from 'vue';
import {
  AlertCircle,
  ArrowLeft,
  Bell,
  Calendar,
  Check,
  ChevronDown,
  ChevronRight,
  CircleDot,
  ClipboardList,
  Eye,
  Filter,
  Gauge,
  History,
  KeyRound,
  LayoutDashboard,
  ListChecks,
  Loader2,
  LogOut,
  Map,
  MessageSquare,
  Package,
  Pencil,
  Plus,
  Search,
  Settings,
  Star,
  Trash2,
  TriangleAlert,
  Users,
  X
} from '@lucide/vue';

const ICONS: Record<string, Component> = {
  // Navigation
  dashboard: LayoutDashboard,
  produkte: Package,
  features: ListChecks,
  priorisierung: Gauge,
  roadmap: Map,
  aufgaben: ClipboardList,
  einstellungen: Settings,
  benutzer: Users,
  abmelden: LogOut,

  // Aktionen
  neu: Plus,
  bearbeiten: Pencil,
  loeschen: Trash2,
  schliessen: X,
  bestaetigen: Check,
  suchen: Search,
  filtern: Filter,
  ansehen: Eye,
  zurueck: ArrowLeft,
  passwort: KeyRound,

  // Zustände und Hinweise
  aufklappen: ChevronDown,
  weiter: ChevronRight,
  laedt: Loader2,
  hinweis: AlertCircle,
  warnung: TriangleAlert,
  status: CircleDot,
  bewertung: Star,
  zeitraum: Calendar,
  kommentar: MessageSquare,
  verlauf: History,
  hinweise: Bell
};

const props = withDefaults(
  defineProps<{
    name: string;
    size?: number;
    /** Strichstaerke — 2 ist der Lucide-Standard, 2.25 wirkt bei kleinen Groessen kraeftiger. */
    strokeWidth?: number;
  }>(),
  { size: 16, strokeWidth: 2 }
);

const component = computed(() => {
  const icon = ICONS[props.name];
  if (!icon) {
    // Kein stiller Fehlschlag: ein Tippfehler im Namen soll auffallen, ohne die
    // Ansicht zu zerlegen.
    console.warn(`Unbekanntes Icon „${props.name}" — in Icon.vue registrieren.`);
  }
  return icon ?? CircleDot;
});
</script>

<template>
  <component
    :is="component"
    :size="size"
    :stroke-width="strokeWidth"
    aria-hidden="true"
    focusable="false"
  />
</template>
